<script lang="ts">
	import type { BlogArticlePage } from '$models/Page/BlogArticlePage';
	import { getSiteContext } from '$lib/context/site';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { formatDate, sanitizeHtml } from '$lib/utils';

	interface Props {
		page: BlogArticlePage;
	}

	let { page }: Props = $props();

	const site = getSiteContext();

	const authorSocials = $derived(
		page.author?.linkedinUrl
			? [{ id: 'linkedin', url: page.author.linkedinUrl, title: 'LinkedIn' }]
			: []
	);
</script>

<div class="blog-hero {page.heroImage ? 'blog-hero--has-image' : ''}">
	<div class="container">
		<div class="blog-hero-inner">
			{#if page.heroImage}
				<figure class="blog-hero-visual">
					<img
						class="blog-hero-image"
						src={page.heroImage.url}
						srcset={getWordPressImageSrcSet(page.heroImage)}
						sizes="100vw"
						alt={page.heroImage.alt}
						width={page.heroImage.width}
						height={page.heroImage.height}
						loading="eager"
						decoding="async"
					/>
				</figure>
			{/if}
			<div class="blog-hero-content">
				<div class="blog-hero-label">
					{formatDate(new Date(page.date), site.locale.code)}
				</div>
				<h1 class="blog-hero-title">
					{@html page.title}
				</h1>
				{#if page.heroText}
					<div class="blog-hero-text rich-text">
						{@html sanitizeHtml(page.heroText)}
					</div>
				{/if}
				{#if page.author}
					<div class="blog-hero-author">
						<div class="blog-hero-author-inner">
							{#if page.author.image}
								<img
									class="blog-hero-author-image"
									src={page.author.image.url}
									alt={page.author.image.alt}
									width={page.author.image.width}
									height={page.author.image.height}
									loading="lazy"
									decoding="async"
								/>
							{/if}
							<div class="blog-hero-author-content">
								<span class="blog-hero-author-name">{page.author.name}</span>
								<span class="blog-hero-author-role">{page.author.jobTitle}</span>
								{#if authorSocials.length > 0}
									<div class="blog-hero-author-socials">
										<div class="blog-hero-author-socials-items">
											{#each authorSocials as item}
												<a
													class="blog-hero-author-socials-item blog-hero-author-socials-item--{item.id}"
													href={item.url}
													target="_blank"
													title={item.title}><span class="screenreader">{item.title}</span></a
												>
											{/each}
										</div>
									</div>
								{/if}
								<!-- TODO: Button maybe translating? -->
								{#if page.author.url}
									<div class="blog-hero-author-button blog-hero-author-button--desktop">
										<a href={page.author.url} class="button button--ghost"> Meer van deze auteur</a>
									</div>
								{/if}
							</div>
						</div>
						{#if page.author.url}
							<div class="blog-hero-author-button blog-hero-author-button--mobile">
								<a href={page.author.url} class="button button--ghost"> Meer van deze auteur </a>
							</div>
						{/if}
					</div>
				{/if}
			</div>
		</div>
	</div>
</div>

<style lang="postcss">
	.blog-hero {
		&-inner {
			padding: 24px 16px 16px;
			background-color: var(--color-primary-light);
			border-radius: 16px;
			overflow: hidden;
		}

		&-image {
			display: block;
			width: 100%;
			height: auto;
			aspect-ratio: 5 / 4;
			object-fit: cover;
			object-position: center center;
			flex-shrink: 0;
		}

		&-content {
			display: flex;
			flex-direction: column;
			gap: 8px;
			max-width: 840px;
			margin: 0 auto;
		}

		&-label {
			font-size: 12px;
		}

		&-title {
			line-height: 1;
		}

		&-author {
			position: relative;
			display: flex;
			flex-direction: column;
			gap: 16px;
			padding: 24px 0;
			margin-top: 16px;

			&:before {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				top: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}

			&::after {
				content: '';
				display: none;
				position: absolute;
				left: 0;
				right: 0;
				bottom: 0;
				height: 2px;
				background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='5' height='2' viewBox='0 0 5 2'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23592e2c'/%3E%3C/svg%3E");
				background-size: 5px 2px;
				background-repeat: repeat-x;
				pointer-events: none;
			}
		}

		&-author-inner {
			display: flex;
			align-items: center;
			gap: 16px;
		}

		&-author-content {
			display: flex;
			flex-direction: column;
			gap: 4px;

			img ~ & {
				padding-block: 4px;
			}
		}

		&-author-image {
			width: 100px;
			height: 90px;
			border-radius: 8px;
			object-fit: cover;
			object-position: center center;
		}

		&-author-name {
			font-family: var(--font-secondary);
			text-transform: uppercase;
			font-size: 18px;
			line-height: 1;
		}

		&-author-role {
			font-size: 14px;
		}

		&-author-socials {
			margin-top: 4px;
			margin-bottom: 20px;

			&-items {
				display: flex;
				align-items: center;
				gap: 10px;
			}

			&-item {
				width: 28px;
				height: 28px;
				display: block;
				background-position: center center;
				background-repeat: no-repeat;
				background-size: 24px;

				&--linkedin {
					background-image: url('data:image/svg+xml;base64,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');
				}
			}
		}

		&-author-button {
			&--mobile {
				display: flex;
				width: 100%;

				.button {
					width: 100%;
				}
			}

			&--desktop {
				display: none;
			}
		}

		&--has-image {
			.blog-hero-inner {
				padding: 0;
			}

			.blog-hero-content {
				padding: 24px 16px;
			}
		}

		@media (--sm) {
			&-author-button {
				&--mobile {
					.button {
						width: auto;
					}
				}
			}
		}

		@media (--md) {
			&-inner {
				padding: 40px 24px;
			}

			&-author-inner {
				gap: 24px;
			}

			&--has-image {
				.blog-hero-inner {
					display: flex;
				}

				.blog-hero-visual {
					width: 40%;
					flex-shrink: 0;
				}

				.blog-hero-image {
					height: 100%;
				}
				.blog-hero-content {
					padding: 24px;
				}
			}
		}

		@media (--lg) {
			&-inner {
				padding: 64px 40px;
			}

			&-label {
				font-size: 14px;
			}

			&-title {
				font-size: 40px;
			}

			&-text {
				font-size: 18px;
			}

			&-author {
				margin-top: 32px;
				padding: 16px 0;

				&::after {
					display: block;
				}
			}

			&-author-inner {
				gap: 40px;
			}

			&-author-image {
				width: 156px;
				height: 156px;
				border-radius: 16px;
			}

			&-author-name {
				font-size: 20px;
			}

			&-author-role {
				font-size: 18px;
			}

			&-author-socials {
				&-items {
					gap: 16px;
				}
			}

			&-author-button {
				&--mobile {
					display: none;
				}

				&--desktop {
					display: flex;
				}
			}

			&--has-image {
				.blog-hero-visual {
					width: 50%;
				}

				.blog-hero-content {
					padding: 32px 40px;
				}
			}
		}

		@media (--xl) {
			&-inner {
				padding: 80px 40px;
			}

			&-title {
				font-size: 48px;
			}

			&--has-image {
				.blog-hero-author {
					margin-top: 24px;
				}

				.blog-hero-content {
					padding: 40px 60px;
				}
			}
		}
	}
</style>
